import type {
  ClosedMessageDocument,
  OpenMessageDocument,
  RecordingMessageDocument,
  TAnyClientQuestionType,
  TQuestionEvaluation,
} from '@hubertai/screening-question-types';
import type { TInterviewSummaryBlockDetail } from './interviewSummary.types';

type TScoresEvaluation = TQuestionEvaluation & { valueId: string };

type TAutoUpdates = {
  interview_id: string;
  validation: {
    pre_validation: boolean;
    score_validation: boolean;
    random_selection_passed: boolean;
    passed: boolean;
    tags: string[];
  };
  ai_detection_summary: TAiDetectionSummary;

  auto_update_eligible: boolean;
  auto_update_executed: boolean;
  scored_categories: {
    category_name: string;
    score: number;
  }[];
  created_at: Date;
  updated_at: Date;
};

type TAiDetectionSummary = {
  is_ai: boolean;
  score: number;
  tag: string;
  version: string;
  underlying_model: string;
  underlying_tag_counts: Record<string, number>;
};

export type TAutoUpdate = {
  interview_id: string;
  validation: {
    pre_validation: boolean;
    score_validation: boolean;
    random_selection_passed: boolean;
    passed: boolean;
    tags: string[];
  };
  ai_detection_summary: TAiDetectionSummary;

  auto_update_eligible: boolean;
  auto_update_executed: boolean;
  scored_categories: {
    category_name: string;
    score: number;
  }[];
  created_at: Date;
  updated_at: Date;
};

type TAiDetectionCategory = {
  is_ai: false;
  score: number;
  tag: string;
  version: string;
  model_id: string;
  missing_feature_list: [];
  per_translated: boolean;
};

type TAutoScoreCategory = {
  ai_detection: TAiDetectionCategory;
  categories: any;
  score: number;
  utilized_qutters: string[]; // e.g. [ "got_other_experiences_help_you_this_role", "tell_me_more_about_experience" ],
  validation: {
    pre_validation: {
      per_qutter: {
        [key: string]: {
          data: object;
          severity_degree: number;
          tags: [];
        };
      };
      severity_degree: number;
      tags: string[];
    };
    score_validation: {
      data: object;
      severity_degree: number;
      tags: string[];
    };
    severity_degree: number;
    tags: string[];
  };
};

export type TAutoScores = {
  interview_id: string;
  application_id: string;
  role_id: string;

  created_at: Date;
  updated_at: Date;
} & Record<string, TAutoScoreCategory>;

export enum ScoreLimits {
  Min = 0,
  Max = 100,
}

export type TChatFilter = {
  isFiltered: boolean;
  filterId: string;
  questionIds: string[];
};

export type TChatCollection = {
  _id: {
    qutter_id: string;
    path?: string;
  };
  questionId: string;
  questionPath?: string;
  questionPathId: string;
  createdAt: Date[];
  expectedLanguage: string[];
  messages: (ClosedMessageDocument | OpenMessageDocument | RecordingMessageDocument)[];
  evaluation?:
    | undefined
    | {
        evaluation?: TScoresEvaluation | TScoresEvaluation[];
        scoreTableDetail?: TInterviewScoreTableDetail | TInterviewScoreTableDetail[];
        summaryDetail?: TInterviewSummaryBlockDetail | TInterviewSummaryBlockDetail[];
      };
  question?: TAnyClientQuestionType;
  summaryDetail?: TInterviewSummaryBlockDetail | TInterviewSummaryBlockDetail[];
};

type TInterviewScoreTableHistory = {
  manually_scored?: boolean;
  auto_updated?: boolean;
  tagged_by: string;
  date: Date;
};

export type TInterviewScoreTableDetail = {
  questionId: string;
  categoryId?: string;
  formId?: string;
  type: 'bonus' | 'weighted';
  scale: [number, number];
  step: number;

  value: number; // value: initial value of the scoring slider
  score?: number; //

  computedValue?: number; // computedValue: initial computed value for deterministic conditions
  displayedScore: number; // displayedScore: computed weighted score - as 0-100
  displayed_score?: number; // displayedScore: computed weighted score - as 0-100
  weight?: number; // weight used: for memory
  edited: boolean; // if edited by scorer

  // Open questions
  predicted?: number; // predictedValue: initial prediction value, 0-10
  validationTags?: string[];
  utilizedQuestionIds?: string[];
  requiredQuestionIds?: string[];
  optionalQuestionIds?: string[];
  aiDetection?: {
    isAi: boolean;
    score: number;
    tag: EIsAITags;
  };
};

export type TNewBuildData = {
  autoScore?: TAutoScores | null | undefined;
  autoUpdate?: TAutoUpdates | null | undefined;
};

export type TInterviewScoreTable = {
  total_score: number;
  qualified: boolean;
  autoQualified: boolean;
  threshold: number;
  weighted_max: number;
  weighted_total: number;
  bonuspoints_max: number;
  bonuspoints_sum: number;
  bonus_booster: number;
  history: TInterviewScoreTableHistory[];
  tagged_by: string;

  role_id: string;
  process_id: string;
  template_id: string;
  interview_id: string;
  application_id: string;
  candidate_id: string;
  job_id: string;
  details: TInterviewScoreTableDetail[];
  accountedCategories: string[];
};

export enum EIsAITags {
  ExtremelyLikely = 'EXTREMELY_LIKELY_AI',
  Likely = 'LIKELY_AI',
  Possibly = 'POSSIBLY_AI',
  Unlikely = 'UNLIKELY_AI',
  ExtremelyUnLikely = 'EXTREMELY_UNLIKELY_AI',
}
